import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { apiGet, apiPost } from "@/lib/shieldx";
import { Loader } from "@/components/shieldx/Primitives";
import { Table } from "@/components/shieldx/Table";
import { StatusBadge, RiskBadge } from "@/components/shieldx/Badges";
import { SAStat, SecBadge } from "@/components/shieldx/SuperAdmin";
import { Eye, LogOut, Building2, Boxes, Siren, ScrollText, UsersRound, Lock } from "lucide-react";

export default function ViewAs() {
  const { orgId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [data, setData] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const [error, setError] = useState("");
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      try {
        const reason = params.get("reason") || "Governance inspection (read-only)";
        const r = await apiPost("/super-admin/view-as/start", { org_id: orgId, reason });
        setSession(r.session);
        const w = await apiGet(`/super-admin/view-as/${r.session.id}`);
        setData(w);
      } catch (e) { setError(e?.response?.data?.detail || "Could not start View-As session"); }
    })();
  }, [orgId, params]);

  useEffect(() => {
    if (!session) return;
    const tick = () => {
      const ms = new Date(session.expires_at).getTime() - Date.now();
      if (ms <= 0) { setRemaining("00:00"); exit(true); return; }
      const m = Math.floor(ms / 60000), s = Math.floor((ms % 60000) / 1000);
      setRemaining(`${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`);
    };
    tick(); const id = setInterval(tick, 1000); return () => clearInterval(id);
    // eslint-disable-next-line
  }, [session]);

  const exit = async (expired) => {
    if (session) { try { await apiPost(`/super-admin/view-as/${session.id}/end`, {}); } catch {} }
    if (expired) toast.info("View-As session expired");
    navigate("/app/super-admin/organizations");
  };

  if (error) return (
    <div className="grid min-h-[60vh] place-items-center"><div className="rounded-2xl border border-rose-500/30 bg-rose-500/5 p-8 text-center"><p className="font-display text-lg text-rose-300">View-As Unavailable</p><p className="mt-1 text-sm text-slate-400">{error}</p><button onClick={() => navigate("/app/super-admin/organizations")} className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white">Return</button></div></div>
  );
  if (!data) return <Loader label="Starting read-only View-As session" />;
  const org = data.organization;

  return (
    <div>
      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
        className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 px-5 py-3.5" data-testid="view-as-banner">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-amber-500/40 bg-amber-500/15 text-amber-300"><Eye className="h-4.5 w-4.5" size={18} /></span>
          <div>
            <p className="flex items-center gap-2 font-display text-sm font-bold text-amber-200">VIEW-AS MODE <span className="inline-flex items-center gap-1 rounded-md bg-slate-900/60 px-2 py-0.5 font-mono text-[10px] text-slate-300"><Lock className="h-3 w-3" /> READ-ONLY</span></p>
            <p className="text-xs text-slate-400">Viewing <b className="text-slate-200">{org.name}</b> · {org.category} · all views are audited</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono text-xs text-amber-300" data-testid="view-as-timer">Expires in {remaining}</span>
          <button onClick={() => exit(false)} data-testid="view-as-exit" className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-200 hover:bg-slate-800"><LogOut className="h-3.5 w-3.5" /> Exit View-As</button>
        </div>
      </motion.div>

      <div className="mb-6 flex items-center gap-3">
        <h1 className="font-display text-2xl font-bold text-slate-50">{org.name}</h1>
        <SecBadge value={org.security_status} />
        <StatusBadge value={org.lifecycle} />
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-5">
        <SAStat label="Assets" value={data.assets.length} icon={Boxes} />
        <SAStat label="Active Incidents" value={data.active_incidents} tone="amber" icon={Siren} />
        <SAStat label="Policies" value={data.policies.length} icon={ScrollText} />
        <SAStat label="Members" value={data.members.length} icon={UsersRound} />
        <SAStat label="Security Rating" value={org.security_score ?? "—"} tone="blue" icon={Building2} />
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5">
          <h3 className="mb-3 font-display text-xs font-semibold uppercase tracking-widest text-slate-400">Assets</h3>
          <Table testid="viewas-assets" empty="No assets" rows={data.assets} columns={[
            { key: "name", label: "Asset", render: (r) => <span className="text-sm text-slate-200">{r.name}</span> },
            { key: "risk_level", label: "Risk", render: (r) => <RiskBadge value={r.risk_level} /> },
            { key: "state", label: "State", render: (r) => <StatusBadge value={r.state || r.protection_status} /> },
          ]} />
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5">
          <h3 className="mb-3 font-display text-xs font-semibold uppercase tracking-widest text-slate-400">Incidents</h3>
          <Table testid="viewas-incidents" empty="No incidents" rows={data.incidents} columns={[
            { key: "incident_id", label: "ID", render: (r) => <span className="font-mono text-xs">{r.incident_id}</span> },
            { key: "risk_level", label: "Risk", render: (r) => <RiskBadge value={r.risk_level} /> },
            { key: "state", label: "State", render: (r) => <StatusBadge value={r.state} /> },
          ]} />
        </div>
      </div>
    </div>
  );
}
